import React from 'react';
import ReactDOM from 'react-dom/client';
import './styles/global.css';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { isNative } from './utils/platform';
import { hydrate as hydrateTokenStore } from './services/tokenStore';
import useAuthStore from './store/authStore';
import { i18nReady } from './i18n/config';

// UI audit mode (development only, REACT_APP_UI_AUDIT=1): fake data for any
// center type, no backend. CRA leaves an unset REACT_APP_ variable as a runtime
// lookup, so a plain require() here would still bundle src/dev/ into main.js;
// a dynamic import keeps it in its own chunk that production never loads.
const auditReady = process.env.REACT_APP_UI_AUDIT === '1'
  ? Promise.all([
    import(/* webpackChunkName: "ui-audit" */ './dev/uiAudit/checks'),
    import(/* webpackChunkName: "ui-audit" */ './dev/uiAudit/crawl'),
    import(/* webpackChunkName: "ui-audit" */ './dev/uiAudit'),
  ]).then(([, , audit]) => audit.install())
  : Promise.resolve();

const root = ReactDOM.createRoot(document.getElementById('root'));

function renderApp() {
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}

if (isNative) {
  // Native: load the JWT/session from secure storage (@capacitor/preferences)
  // into the in-memory mirror and rehydrate the auth store before first paint,
  // so the app doesn't flash the login screen on launch.
  Promise.all([
    hydrateTokenStore()
      .then(() => useAuthStore.getState().bootstrap())
      .catch(() => { /* render anyway; user re-authenticates if needed */ }),
    i18nReady,
    auditReady,
  ]).finally(renderApp);
} else {
  // Web: token already read from localStorage. Wait only for the active
  // language's translation chunk (lazy since the bundle-budget fix) so the
  // first paint never shows raw i18n keys. i18nReady never rejects.
  Promise.all([i18nReady, auditReady]).finally(renderApp);
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// Service worker registration — Phase 1.5/7.4.
// Skipped in dev (CRA's webpack-dev-server already manages the page lifecycle
// and the SW would cache stale HMR chunks). Production builds register the
// SW after page load so it doesn't compete for the main thread on first paint.
(() => {
  if (process.env.NODE_ENV !== 'production') return;
  // UI-audit builds are rebuilt and reloaded constantly; a SW would serve stale ones.
  if (process.env.REACT_APP_UI_AUDIT === '1') return;
  // Never register the web SW inside Capacitor — it serves assets from its own
  // local origin and an intercepting SW causes stale-asset/caching bugs.
  if (isNative) return;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js').catch((err) => {
      // Non-fatal — app keeps working without SW.
      console.warn('[SW] registration failed:', err);
    });
  });
})();

// Reload stale content when the app returns to foreground (home screen / PWA).
// When added to home screen, the OS freezes the page in background and restores
// the old snapshot. This ensures fresh data after being away for 5+ minutes.
(() => {
  // Skip on native: Capacitor has its own resume lifecycle, and a blanket
  // window.location.reload() on every foreground feels like a cold start.
  if (isNative) return;
  let hiddenAt = 0;
  const STALE_THRESHOLD = 5 * 60 * 1000; // 5 minutes

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now();
    } else if (document.visibilityState === 'visible' && hiddenAt > 0) {
      const elapsed = Date.now() - hiddenAt;
      if (elapsed >= STALE_THRESHOLD) {
        window.location.reload();
      }
    }
  });
})();
